import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, map, tap } from 'rxjs';
import { Data, GetPermissionsListRequest, GetUserPermissionsListRequest, PermissionsAddUpdateRequest, PermissionsResponse, UserPermissionsResponse } from './permissions.interface';

@Injectable({providedIn: 'root'})
export class PermissionsService {

    private http = inject(HttpClient);
    private apiUrl = environment.int365_dom;


    permissionsList = signal<Data[]>([]);
    userPermissionsList = signal<Data[]>([]);
    initialUserPermissionsList = signal<Data[]>([]);

    /**
     * Obtiene los permisos del usuario logueado consultando el endpoint en cada llamada.
     * No modifica el signal global userPermissionsList para no contaminar el estado del usuario en administración.
     * @param request Datos del usuario logueado
     * @returns Observable con la lista de permisos del usuario logueado
     */
    getLoggedUserPermissions(request: GetUserPermissionsListRequest): Observable<Data[]> {
        return this.http.get<UserPermissionsResponse>(`${this.apiUrl}/api/v2/Users/permissions`,
        { params: { ...request } }).pipe(
            map((response) => response.data.permissions)
        );
    }

    getPermissions(request: GetPermissionsListRequest): Observable<PermissionsResponse> {
        return this.http.get<PermissionsResponse>(`${this.apiUrl}/api/v1/permissions`, { params: { ...request } }).pipe(
            tap((response: PermissionsResponse) => {
                this.permissionsList.set(response.data);
            })
        );
    }

    getUserPermissions(request: GetUserPermissionsListRequest): Observable<UserPermissionsResponse> {
        return this.http.get<UserPermissionsResponse>(`${this.apiUrl}/api/v2/Users/permissions`,
        { params: { ...request } }).pipe(
            tap((response: UserPermissionsResponse) => {
                const permissions = response.data.permissions;
                this.userPermissionsList.set(permissions);
                this.initialUserPermissionsList.set(permissions);
            })
        );
    }



     /**
   * Agrega o actualiza los permisos de un usuario
   * @param request Datos de los permisos del usuario
   * @returns Observable con la respuesta del servidor que indica si se agregaron o actualizaron los
   * permisos satisfactoriamente
   */
  addUpdateUserPermissions(
    request: PermissionsAddUpdateRequest
  ): Observable<PermissionsResponse> {
    return this.http.put<PermissionsResponse>(
      `${this.apiUrl}/api/v2/Users/Permissions`,
      request
    );
  }

  /**
   * Limpia los permisos del usuario en memoria.
   */
  resetUserPermissions(): void {
    this.userPermissionsList.set([]);
    this.initialUserPermissionsList.set([]);
  }

  /**
   * Sincroniza el estado inicial con los permisos actuales del usuario.
   */
  syncInitialUserPermissions(): void {
    this.initialUserPermissionsList.set([...this.userPermissionsList()]);
  }

  /**
   * Verifica si el usuario actual posee un permiso según su descripción.
   * @param permissionDescription Descripción del permiso a validar
   * @returns Indica si el permiso existe en userPermissionsList
   */
  hasUserPermission(permissionDescription: string): boolean {
    return this.userPermissionsList().some(
      (permission) => permission.description === permissionDescription
    );
  }

  /**
   * Verifica si los permisos seleccionados difieren de los permisos actuales del usuario.
   */
  hasPermissionChanges(
    selectedPermissionIds: string[],
    currentPermissions: Data[]
  ): boolean {
    const currentPermissionIds = currentPermissions.map(
      (permission) => permission.externalId
    );

    return (
      selectedPermissionIds.length !== currentPermissionIds.length ||
      selectedPermissionIds.some(
        (permissionId) => !currentPermissionIds.includes(permissionId)
      ) ||
      currentPermissionIds.some(
        (permissionId) => !selectedPermissionIds.includes(permissionId)
      )
    );
  }


}
